import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Beaker, User, Mail, Lock } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const res = await api.post("/auth/register", form);
      setUser(res.data.user);
      toast.success("Account created. Complete your profile to unlock pricing.");
      if (res.data.verification_link) {
        toast.info("Dev mode: verification link logged to server console.");
      }
      navigate("/onboarding", { replace: true });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="register-page" className="min-h-[80vh] flex items-center justify-center bg-slate-50 px-6 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white border border-slate-200 rounded-md p-10">
          <div className="flex items-center gap-3 mb-6">
            <img src="/logo.webp" alt="" className="h-11 w-11 object-contain"/>
            <div>
              <div className="font-display text-base font-semibold tracking-tight text-[#0e2f6b]">Peps<span className="text-blue-500">4</span>Pets</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-blue-700">Create Account</div>
            </div>
          </div>
          <h1 className="font-display text-3xl font-semibold text-slate-900 tracking-tight">Open a research account.</h1>
          <p className="mt-2 text-slate-600 text-sm">Unlock pricing, track orders, download COAs.</p>

          <form onSubmit={submit} className="mt-6 space-y-3">
            <div className="relative">
              <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={1.75}/>
              <Input data-testid="register-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="h-11 pl-9 rounded-md border-slate-300" />
            </div>
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={1.75}/>
              <Input data-testid="register-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@lab.com" className="h-11 pl-9 rounded-md border-slate-300" />
            </div>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={1.75}/>
              <Input data-testid="register-password" type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password (min 8 chars)" className="h-11 pl-9 rounded-md border-slate-300" />
            </div>
            {err && <div data-testid="register-error" className="text-xs text-red-600 border border-red-200 bg-red-50 rounded-md p-2">{err}</div>}
            <Button data-testid="register-submit" type="submit" disabled={busy} className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white rounded-md">
              {busy ? "Creating account…" : "Create account"}
            </Button>
            <div className="text-xs text-center pt-2 text-slate-600">
              Already have an account? <Link to="/login" className="text-blue-700 font-medium">Sign in</Link>
            </div>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">or</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <Button
            data-testid="google-signup-btn"
            onClick={() => {
              const redirectUrl = window.location.origin + "/dashboard";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            variant="outline"
            className="w-full h-11 border-slate-300 text-slate-800 rounded-md"
          >
            Sign up with Google
          </Button>

          <p className="mt-4 text-[11px] text-center text-slate-400">
            No password to remember. One-click sign-in via Google.
          </p>
        </div>
      </div>
    </div>
  );
}
